(function () {
  'use strict';

  let operationDepth = 0;
  const ACTIVE_CLASS = 'new-logo-injected-loading';
  const TEMP_ACTION_ATTR = 'data-new-logo-temp-action-skeleton';
  const TEMP_NOTE_ATTR = 'data-new-logo-temp-note-skeleton';
  const TEMP_DIGITISATION_ATTR = 'data-new-logo-temp-digitisation-skeleton';
  const MIN_VISIBLE_MS = 140;
  const AFTER_UPDATE_DELAY_MS = 110;
  const FAILSAFE_MS = 2800;
  const DIGITISATION_FEE_VARIANT_ID = '55420858270081';
  const PHASE_ACTIONS = 'actions';
  const PHASE_PRICING = 'pricing';
  const PHASE_DIGITISATION = 'digitisation';
  const ALL_SYNC_PHASES = [PHASE_ACTIONS, PHASE_PRICING, PHASE_DIGITISATION];

  let activeStartedAt = 0;
  let clearTimer = null;
  let settleTimer = null;
  let failsafeTimer = null;
  let drawerObserver = null;
  let bodyObserver = null;
  let observedBody = null;
  let pendingPhases = new Set();
  let waitingForPhases = false;

  function getDrawer() {
    return document.querySelector('cart-drawer');
  }

  function isDrawerOpen(drawer) {
    return Boolean(drawer && drawer.hasAttribute('open'));
  }

  function getDrawerBody(drawer) {
    if (!drawer) return null;
    return drawer.querySelector('[id^="CartDrawerBody-"]');
  }

  function hasRuntimeCustomisationByHandle(handle) {
    const safeHandle = String(handle || '').trim();
    if (!safeHandle) return false;

    const runtime = window.NewLogoRuntime || {};
    const byHandle = runtime.customisationOptionsByHandle || {};
    if (Object.prototype.hasOwnProperty.call(byHandle, safeHandle)) {
      const value = byHandle[safeHandle];
      return Array.isArray(value) ? value.length > 0 : Boolean(value);
    }

    const lowerHandle = safeHandle.toLowerCase();
    const matchedKey = Object.keys(byHandle).find((key) => String(key || '').trim().toLowerCase() === lowerHandle);
    if (!matchedKey) return false;
    const matchedValue = byHandle[matchedKey];
    return Array.isArray(matchedValue) ? matchedValue.length > 0 : Boolean(matchedValue);
  }

  function findDigitisationInsertTarget(drawer) {
    const cartItems = drawer?.querySelector('cart-items');
    if (!cartItems || !cartItems.parentElement) return null;
    return { parent: cartItems.parentElement, before: cartItems };
  }

  function ensureInjectedSkeletonTargets() {
    const drawer = getDrawer();
    if (!isDrawerOpen(drawer)) return;

    const cartItems = drawer.querySelectorAll('.cart-item');
    cartItems.forEach((item) => {
      const details = item.querySelector('.cart-item__details');
      if (!details) return;
      const hasInjectedAction =
        Boolean(details.querySelector('.new-logo-action-host, .new-logo-cart-action')) ||
        item.querySelector('.new-logo-cart-action') != null;
      const isCustomisable = hasRuntimeCustomisationByHandle(item.getAttribute('data-handle'));
      // hasRuntimeCustomisationByHandle reads a map rendered server-side at page
      // load, so after the theme swaps in fresh markup a row that inject.js will
      // happily re-inject cannot be re-covered by that test alone. During a
      // bracketed operation, mark every row.
      if (operationDepth === 0 && !hasInjectedAction && !isCustomisable) return;

      let actionHost = details.querySelector('.new-logo-action-host');
      if (!actionHost) {
        const options = details.querySelector('.cart-item__options');
        const mount = options || details.querySelector('.cart-item__action') || details;
        if (!mount.querySelector(`[${TEMP_ACTION_ATTR}]`)) {
          const tempAction = document.createElement('div');
          tempAction.className = 'new-logo-action-host new-logo-action-host--loading';
          tempAction.setAttribute(TEMP_ACTION_ATTR, 'true');
          mount.insertBefore(tempAction, mount.firstChild || null);
        }
      } else {
        actionHost.classList.add('new-logo-action-host--loading');
      }

      const prices = details.querySelector('.cart-item__prices');
      if (prices) {
        const existingNote = prices.querySelector('.new-logo-extra-note');
        if (existingNote) {
          existingNote.classList.add('new-logo-extra-note--loading');
        } else if (!prices.querySelector(`[${TEMP_NOTE_ATTR}]`)) {
          const tempNote = document.createElement('div');
          tempNote.className = 'new-logo-extra-note--loading text-subtext';
          tempNote.style.textAlign = 'right';
          tempNote.style.width = '100%';
          tempNote.setAttribute(TEMP_NOTE_ATTR, 'true');
          prices.appendChild(tempNote);
        }
      }
    });

    const existingDigitisation = drawer.querySelector('[data-digitisation-fee-inline]');
    if (existingDigitisation) {
      existingDigitisation.classList.add('new-logo-digitisation-inline--loading');
      return;
    }

    if (drawer.querySelector(`[${TEMP_DIGITISATION_ATTR}]`)) return;

    const hasHiddenFeeRow = drawer.querySelector(`.cart-item[data-variant-id="${DIGITISATION_FEE_VARIANT_ID}"]`);
    if (!hasHiddenFeeRow) return;

    const target = findDigitisationInsertTarget(drawer);
    if (!target || !target.parent) return;

    const tempPanel = document.createElement('div');
    tempPanel.className = 'new-logo-digitisation-inline new-logo-digitisation-inline--loading';
    tempPanel.setAttribute(TEMP_DIGITISATION_ATTR, 'true');

    if (target.before) {
      target.parent.insertBefore(tempPanel, target.before);
    } else {
      target.parent.appendChild(tempPanel);
    }
  }

  function cleanupTemporarySkeletons() {
    const drawer = getDrawer();
    if (!drawer) return;

    drawer.querySelectorAll(`[${TEMP_ACTION_ATTR}]`).forEach((el) => {
      if (el.querySelector('.new-logo-cart-action')) {
        el.removeAttribute(TEMP_ACTION_ATTR);
        el.classList.remove('new-logo-action-host--loading');
        return;
      }
      el.remove();
    });

    drawer.querySelectorAll(`[${TEMP_NOTE_ATTR}]`).forEach((el) => {
      const hasContent = String(el.textContent || '').trim().length > 0;
      if (hasContent && el.classList.contains('new-logo-extra-note')) {
        el.removeAttribute(TEMP_NOTE_ATTR);
        el.classList.remove('new-logo-extra-note--loading');
        return;
      }
      el.remove();
    });

    drawer.querySelectorAll(`[${TEMP_DIGITISATION_ATTR}]`).forEach((el) => {
      const hasContent = String(el.textContent || '').trim().length > 0;
      if (hasContent && el.hasAttribute('data-digitisation-fee-inline')) {
        el.removeAttribute(TEMP_DIGITISATION_ATTR);
        el.classList.remove('new-logo-digitisation-inline--loading');
        return;
      }
      el.remove();
    });

    drawer
      .querySelectorAll('.new-logo-action-host--loading, .new-logo-extra-note--loading, .new-logo-digitisation-inline--loading')
      .forEach((el) => {
        el.classList.remove('new-logo-action-host--loading', 'new-logo-extra-note--loading', 'new-logo-digitisation-inline--loading');
      });
  }

  function queueFailsafe() {
    clearTimeout(failsafeTimer);
    failsafeTimer = setTimeout(() => {
      // Clear the bracket too. If an operation started and never ended — a
      // rejected request that escaped its handler, say — leaving the depth
      // raised would block every future phase signal and make the skeleton
      // depend on this timer from then on.
      operationDepth = 0;
      deactivate(true);
    }, FAILSAFE_MS);
  }

  function activate() {
    observeDrawerOpenState();
    observeBodyMutations();

    const drawer = getDrawer();
    if (!isDrawerOpen(drawer)) return;

    clearTimeout(clearTimer);
    clearTimeout(settleTimer);

    // Anchor the failsafe to the start of the covered period, not to the last
    // activate(). activate() is bound to cart:refresh, which the operation
    // itself dispatches, so re-arming here slid the 2800ms deadline out every
    // time and the failsafe stopped being a bound on anything.
    const wasCovered = drawer.classList.contains(ACTIVE_CLASS);
    if (!wasCovered) {
      activeStartedAt = Date.now();
    }

    drawer.classList.add(ACTIVE_CLASS);
    drawer.setAttribute('aria-busy', 'true');
    ensureInjectedSkeletonTargets();
    // During an operation the failsafe is anchored — heartbeats extend it, and
    // re-arming on every activate() let the operation's own cart:refresh slide
    // it out indefinitely. Outside one, re-arm freely: it is the only bound on
    // a cover left behind by a cancelled reveal.
    if (!wasCovered || operationDepth === 0) queueFailsafe();
  }

  function deactivate(force) {
    const drawer = getDrawer();
    if (!drawer || !drawer.classList.contains(ACTIVE_CLASS)) {
      clearTimeout(failsafeTimer);
      failsafeTimer = null;
      return;
    }

    const elapsed = Date.now() - activeStartedAt;
    const wait = force ? 0 : Math.max(0, MIN_VISIBLE_MS - elapsed);

    clearTimeout(clearTimer);
    clearTimer = setTimeout(() => {
      const latestDrawer = getDrawer();
      if (!latestDrawer) return;

      latestDrawer.classList.remove(ACTIVE_CLASS);
      latestDrawer.removeAttribute('aria-busy');
      cleanupTemporarySkeletons();
      waitingForPhases = false;
      pendingPhases = new Set();

      clearTimeout(failsafeTimer);
      failsafeTimer = null;
    }, wait);
  }

  function deactivateSoon(delay) {
    clearTimeout(settleTimer);
    settleTimer = setTimeout(() => {
      deactivate(false);
    }, Math.max(0, Number(delay) || 0));
  }

  function beginCartOperation() {
    operationDepth += 1;
    activate();
    beginPhaseWaitCycle();
    // An operation that begins with the drawer shut gets no failsafe from
    // activate() (it bails when the drawer is closed); arm it here so a stall
    // is still bounded whichever state the drawer was in.
    queueFailsafe();
  }

  function endCartOperation() {
    operationDepth = Math.max(0, operationDepth - 1);
    // If the injector says no operation is running, believe it — an
    // unbalanced start (from any historical overlap) must not strand the
    // depth above zero and block every future phase signal.
    if (document.documentElement.getAttribute('data-new-logo-cart-busy') !== '1') {
      operationDepth = 0;
    }
    if (operationDepth > 0) return;
    // Everything the operation was going to change has landed; reveal once.
    waitingForPhases = false;
    pendingPhases = new Set();
    deactivateSoon(AFTER_UPDATE_DELAY_MS);
  }

  function beginPhaseWaitCycle() {
    const drawer = getDrawer();
    if (!drawer || !drawer.classList.contains(ACTIVE_CLASS)) return;
    waitingForPhases = true;
    pendingPhases = new Set(ALL_SYNC_PHASES);
  }

  function markPhaseComplete(phase) {
    const drawer = getDrawer();
    if (!drawer || !drawer.classList.contains(ACTIVE_CLASS)) return;
    // A bracketed operation owns the skeleton from start to end. Its individual
    // steps each finish at different moments, and letting any one of them lift
    // the cover is what showed the half-updated cart between them.
    if (operationDepth > 0) return;
    if (!waitingForPhases) return;
    if (!pendingPhases.has(phase)) return;

    pendingPhases.delete(phase);
    if (pendingPhases.size > 0) return;

    waitingForPhases = false;
    deactivateSoon(AFTER_UPDATE_DELAY_MS);
  }

  function isCartMutationRequest(resource, init) {
    try {
      const inputUrl =
        typeof resource === 'string'
          ? resource
          : resource?.url || resource?.toString?.() || '';
      if (!inputUrl) return false;

      const parsed = new URL(inputUrl, window.location.origin);
      const path = parsed.pathname || '';
      // Keep skeleton for add flows only. Quantity change/update in cart drawer
      // now updates injected UI synchronously and looks smoother without skeleton mode.
      if (!/\/cart\/add(\.js)?$/.test(path)) return false;

      const method = String(init?.method || resource?.method || 'GET').toUpperCase();
      return method === 'POST' || method === 'PUT' || method === 'PATCH';
    } catch (error) {
      return false;
    }
  }

  function onPotentialUpdateTrigger(event) {
    const drawer = getDrawer();
    if (!isDrawerOpen(drawer)) return;

    const trigger = event.target?.closest?.(
      'cart-drawer [is="cart-remove-item"], cart-drawer .btn-remove, cart-drawer [data-new-logo-remove]'
    );
    if (!trigger) return;
    activate();
  }

  function onQuantityInputChange(event) {
    // Intentionally no-op to avoid skeleton jump during quantity edits.
    void event;
  }

  function patchFetchForCartMutations() {
    const originalFetch = window.fetch;
    if (typeof originalFetch !== 'function') return;
    if (window.__newLogoInjectedSkeletonFetchPatched) return;
    window.__newLogoInjectedSkeletonFetchPatched = true;

    window.fetch = function patchedFetch(resource, init) {
      if (isCartMutationRequest(resource, init)) {
        activate();
      }
      return originalFetch.apply(this, arguments);
    };
  }

  function observeDrawerOpenState() {
    const drawer = getDrawer();
    if (!drawer || drawerObserver) return;

    drawerObserver = new MutationObserver((mutations) => {
      if (!mutations.some((mutation) => mutation.attributeName === 'open')) return;
      if (drawer.hasAttribute('open')) {
        observeBodyMutations();
        // A save runs with the drawer shut and opens it at the end of the
        // operation. Without this the drawer opens uncovered onto a cart that
        // is still settling, and the renders that land next read as the cart
        // refreshing by itself.
        if (operationDepth > 0) activate();
        return;
      }
      deactivate(true);
    });

    drawerObserver.observe(drawer, {
      attributes: true,
      attributeFilter: ['open'],
    });
  }

  function observeBodyMutations() {
    const drawer = getDrawer();
    const body = getDrawerBody(drawer);
    if (!body) return;

    if (bodyObserver && observedBody === body) return;
    if (bodyObserver) {
      bodyObserver.disconnect();
    }

    observedBody = body;
    bodyObserver = new MutationObserver(() => {
      const latestDrawer = getDrawer();
      if (!latestDrawer || !latestDrawer.classList.contains(ACTIVE_CLASS)) return;
      ensureInjectedSkeletonTargets();
    });

    bodyObserver.observe(body, {
      childList: true,
      subtree: true,
    });
  }

  function init() {
    patchFetchForCartMutations();
    observeDrawerOpenState();
    observeBodyMutations();

    document.addEventListener('click', onPotentialUpdateTrigger, true);
    document.addEventListener('change', onQuantityInputChange, true);

    document.addEventListener('cart:refresh', () => activate());
    document.addEventListener('new-logo:cart-operation-start', () => beginCartOperation());
    document.addEventListener('new-logo:cart-operation-end', () => endCartOperation());
    document.addEventListener('new-logo:cart-operation-heartbeat', () => {
      if (operationDepth > 0) queueFailsafe();
    });
    document.addEventListener('cart:updated', () => beginPhaseWaitCycle());
    document.addEventListener('new-logo:cart-actions-synced', (event) => {
      // 'already-running' means a sync was in flight and this call bowed out —
      // not that the work is done. The run that is still going emits
      // 'completed' when it finishes, and queueFailsafe() covers the case where
      // it never does, so waiting is safe.
      if (String(event?.detail?.reason || '') === 'already-running') return;
      markPhaseComplete(PHASE_ACTIONS);
    });
    document.addEventListener('new-logo:cart-pricing-synced', (event) => {
      // 'completed-initial' is emitted after the first paint but *before* the
      // digitisation fee reconcile runs. Treating it as the pricing phase being
      // done lifted the skeleton over a cart that was still settling, so what
      // it revealed was the pre-update row — a removed customisation looking
      // like it had come back — until the final refresh landed a second later.
      if (String(event?.detail?.reason || '') === 'completed-initial') return;
      markPhaseComplete(PHASE_PRICING);
    });
    document.addEventListener('new-logo:digitisation-inline-synced', () => markPhaseComplete(PHASE_DIGITISATION));
    window.addEventListener('logo-customiser:saved', () => {
      // A bracketed save owns its reveal; this legacy uncover raced it.
      if (operationDepth > 0) return;
      deactivateSoon(AFTER_UPDATE_DELAY_MS);
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
